import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Agent from 'models/agent';
import Chip from 'components/common/chip';
import MultiSelect from 'components/lib/multi_select';
import ProfileAvatar from 'components/lib/profile_avatar';

/**
 * @visibleName Agent Select
 */

const AgentSelect = props => {
  const selectedAgents = getAgentObjects(props.selectedAgents, props.currentAgentId);
  const allAgents = sortAgents(getAgentObjects(props.allAgents, props.currentAgentId));
  const agentSelectClasses = classnames('agent-select', props.agentSelectClasses);

  return (
    <div className="agent-select-container">
      {props.header ? <div className="agent-select-header">{props.header}</div> : null}
      <MultiSelect
        className={agentSelectClasses}
        initialSelectedOptions={selectedAgents}
        options={allAgents}
        onChange={createUpdateHandler(props)}
        placeholder="Agent name"
        optionRenderer={optionRenderer}
        filterOption={filterOption}
        valueComponent={renderChipValue}
      />
    </div>
  );
};

function filterOption(option, filter) {
  filter = filter.toLowerCase();
  return option.agentEmail.toLowerCase().indexOf(filter) > -1 || option.label.toLowerCase().indexOf(filter) > -1;
}

function optionRenderer({ isCurrentAgent, label, agentEmail }) {
  if (isCurrentAgent) {
    return (
      <div className="Select-option-wrapper Select-option-wrapper-current-agent">
        <div className="Select-option-avatar">
          <ProfileAvatar profile={{ name: label }} noWrapper />
        </div>
        <div className="Select-option-info">
          <div className="Select-option-row" title={label}>
            <span className="Select-option-label">{`${label} (You)`}</span>
          </div>
          <div className="Select-option-row" title={agentEmail}>
            <span className="Select-option-sublabel">{agentEmail}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Select-option-wrapper">
      <div className="Select-option-row" title={label || agentEmail}>
        <span className="Select-option-label">{label || agentEmail}</span>
      </div>
      <div className="Select-option-row" title={agentEmail}>
        <span className="Select-option-sublabel">{agentEmail}</span>
      </div>
    </div>
  );
}

const ChipValue = styled(Chip)`
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  margin-right: ${p => p.theme.spacing.inlineSmall};
  max-width: 40%;
`;

function renderChipValue({ id, disabled, value, onRemove }) {
  return (
    <ChipValue
      onMouseDown={onChipDown}
      key={id}
      label={value.label || value.agentEmail}
      onDelete={() => onRemove(value)}
      disabled={disabled}
    />
  );
}
function onChipDown(e) {
  // Need to handle onMouseDown otherwise it opens the menu every click even for delete
  e.stopPropagation();
}

function createUpdateHandler(props) {
  return agents => {
    props.updateSelectedAgents(_.map(agents, 'id'));
  };
}

export function getAgentObjects(agents, currentAgentId) {
  if (!agents || !agents.length) {
    return [];
  }

  return _.map(agents, agent => {
    return {
      id: agent.id,
      label: agent.name,
      agentEmail: agent.email,
      isCurrentAgent: currentAgentId === agent.id,
    };
  });
}

export function sortAgents(agents) {
  return _.orderBy(
    agents,
    ['isCurrentAgent', agent => (agent.label || agent.agentEmail).toLowerCase()],
    ['desc', 'asc', 'asc']
  );
}

optionRenderer.propTypes = {
  isCurrentAgent: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  agentEmail: PropTypes.string.isRequired,
};

AgentSelect.propTypes = {
  /** Array of Agent Models */
  allAgents: PropTypes.arrayOf(PropTypes.instanceOf(Agent)).isRequired,
  /** Array of Agent Models that should be marked as selected */
  selectedAgents: PropTypes.arrayOf(PropTypes.instanceOf(Agent)).isRequired,
  currentAgentId: PropTypes.string.isRequired,
  updateSelectedAgents: PropTypes.func.isRequired,
  agentSelectClasses: PropTypes.string,
  header: PropTypes.string,
};

export default AgentSelect;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/common/agent_select.jsx